/**
 * Product Tabs Component
 * Handles tab switching functionality for product categories
 */

class ProductTabsComponent extends HTMLElement {
  constructor() {
    super();
    this.tabs = this.querySelectorAll('.product-tabs__tab');
    this.panels = this.querySelectorAll('.product-tabs__panel');
    this.init();
  }

  init() {
    if (this.tabs.length === 0 || this.panels.length === 0) return;

    // Add click event listeners to tabs
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', (e) => this.handleTabClick(e));
      
      // Keyboard navigation
      tab.addEventListener('keydown', (e) => this.handleKeyDown(e));
    });

    // Set initial active state
    this.setActiveTab(this.tabs[0]);
  }

  handleTabClick(event) {
    const clickedTab = event.currentTarget;
    this.setActiveTab(clickedTab);
  }

  handleKeyDown(event) {
    const currentTab = event.currentTarget;
    const currentIndex = Array.from(this.tabs).indexOf(currentTab);
    let newIndex = currentIndex;

    // Arrow key navigation
    switch (event.key) {
      case 'ArrowLeft':
        newIndex = currentIndex > 0 ? currentIndex - 1 : this.tabs.length - 1;
        event.preventDefault();
        break;
      case 'ArrowRight':
        newIndex = currentIndex < this.tabs.length - 1 ? currentIndex + 1 : 0;
        event.preventDefault();
        break;
      case 'Home':
        newIndex = 0;
        event.preventDefault();
        break;
      case 'End':
        newIndex = this.tabs.length - 1;
        event.preventDefault();
        break;
      default:
        return;
    }

    // Focus and activate new tab
    this.tabs[newIndex].focus();
    this.setActiveTab(this.tabs[newIndex]);
  }

  setActiveTab(activeTab) {
    const tabId = activeTab.dataset.tabId;
    const targetPanel = this.querySelector(`#tab-panel-${tabId}`);

    if (!targetPanel) return;

    // Update tabs
    this.tabs.forEach((tab) => {
      const isActive = tab === activeTab;
      tab.classList.toggle('active', isActive);
      tab.setAttribute('aria-selected', isActive);
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    // Update panels
    this.panels.forEach((panel) => {
      const isActive = panel === targetPanel;
      panel.classList.toggle('active', isActive);
      
      if (isActive) {
        panel.removeAttribute('hidden');
        panel.setAttribute('tabindex', '0');
      } else {
        panel.setAttribute('hidden', '');
        panel.setAttribute('tabindex', '-1');
      }
    });

    // Announce change to screen readers
    this.announceTabChange(activeTab.textContent);
  }

  announceTabChange(tabName) {
    // Create or update live region for screen reader announcements
    let liveRegion = document.getElementById('tab-live-region');
    
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'tab-live-region';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.className = 'visually-hidden';
      document.body.appendChild(liveRegion);
    }

    // This will be handled by theme translations in the future
    liveRegion.textContent = `${tabName}`;
  }
}

// Register custom element
if (!customElements.get('product-tabs-component')) {
  customElements.define('product-tabs-component', ProductTabsComponent);
}

